import { Instagram, Facebook, MapPin } from "lucide-react";
import { WhatsAppIcon, TikTokIcon } from "./Icons";
import { scrollToId } from "./Header";
import {
  NAV_LINKS,
  SOCIALS,
  WHATSAPP_LINK,
  WHATSAPP_DISPLAY,
  EMAIL,
  LOCATION,
} from "../../data/site";
import { useLang } from "../../i18n/LanguageContext";

const SOCIAL_BUTTONS = [
  { label: "Instagram", href: SOCIALS.instagram, Icon: Instagram, id: "instagram" },
  { label: "TikTok", href: SOCIALS.tiktok, Icon: TikTokIcon, id: "tiktok" },
  { label: "Facebook", href: SOCIALS.facebook, Icon: Facebook, id: "facebook" },
  { label: "WhatsApp", href: WHATSAPP_LINK, Icon: WhatsAppIcon, id: "whatsapp" },
  { label: "Maps", href: SOCIALS.maps, Icon: MapPin, id: "maps" },
];

const Footer = () => {
  const { t } = useLang();
  const f = t.footer;
  return (
  <footer data-testid="site-footer" className="grain relative bg-forest text-cream">
    <div className="relative z-10 mx-auto max-w-[1400px] px-5 md:px-10">
      <div className="grid grid-cols-1 gap-12 py-16 md:grid-cols-4 md:py-20">
        {/* Brand */}
        <div className="md:col-span-1">
          <div className="flex items-baseline gap-2">
            <span className="font-serif text-2xl tracking-wide text-cream">TROPICAL</span>
            <span className="font-serif text-2xl tracking-wide text-gold">VANILLA</span>
          </div>
          <p className="overline mt-1 text-[0.6rem] text-cream/50">Indonesian Tropical Heritage</p>
          <p className="mt-6 max-w-xs text-sm leading-relaxed text-cream/60">
            {f.tagline}
          </p>
        </div>

        {/* Quick links */}
        <div>
          <p className="overline text-gold">{f.quickLinks}</p>
          <ul className="mt-5 space-y-3">
            {NAV_LINKS.map((l) => (
              <li key={l.id}>
                <button
                  data-testid={`footer-link-${l.id}`}
                  onClick={() => scrollToId(l.id)}
                  className="text-sm text-cream/70 transition-colors hover:text-gold"
                >
                  {t.nav[l.id]}
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* Products */}
        <div>
          <p className="overline text-gold">{f.products}</p>
          <ul className="mt-5 space-y-3 text-sm text-cream/70">
            {f.productList.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        </div>

        {/* Contact */}
        <div>
          <p className="overline text-gold">{f.contact}</p>
          <ul className="mt-5 space-y-3 text-sm text-cream/70">
            <li className="flex items-start gap-2">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
              <span>{LOCATION.address}</span>
            </li>
            <li>
              <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="hover:text-gold">
                WhatsApp: {WHATSAPP_DISPLAY}
              </a>
            </li>
            <li>
              <a href={`mailto:${EMAIL}`} className="hover:text-gold break-all">
                {EMAIL}
              </a>
            </li>
          </ul>

          <div className="mt-6 flex flex-wrap gap-3">
            {SOCIAL_BUTTONS.map(({ label, href, Icon, id }) => (
              <a
                key={id}
                data-testid={`social-${id}`}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
                className="flex h-10 w-10 items-center justify-center rounded-full border border-cream/25 text-cream/80 transition-colors duration-300 hover:border-gold hover:bg-gold hover:text-white"
              >
                <Icon className="h-4 w-4" />
              </a>
            ))}
          </div>
        </div>
      </div>

      <div className="flex flex-col items-center justify-between gap-4 border-t border-cream/15 py-6 md:flex-row">
        <p className="text-xs text-cream/50">
          © {new Date().getFullYear()} Tropical Vanilla Co. {f.rights}
        </p>
        <p className="overline text-[0.6rem] text-cream/40">{f.madeIn}</p>
      </div>
    </div>
  </footer>
  );
};

export default Footer;
